/** ImageAdjustModal — pan / zoom / fit-fill / crop editor for playbook images.
 * Bakes the adjusted result into a new base64 image, so PDFs match exactly. */
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, RotateCcw, Check, Loader2 } from "lucide-react";

const A4 = 210 / 297;
const ASPECTS = [
  { label: "Original", v: 0 },
  { label: "A4 page", v: A4 },
  { label: "1 : 1", v: 1 },
  { label: "4 : 3", v: 4 / 3 },
  { label: "3 : 4", v: 3 / 4 },
  { label: "16 : 9", v: 16 / 9 },
];

export function ImageAdjustModal({ src, mode = "inline", onApply, onClose }) {
  const [img, setImg] = useState(null);
  const [fit, setFit] = useState("fill");
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [aspect, setAspect] = useState(mode === "full" ? A4 : 0);
  const drag = useRef(null);

  useEffect(() => {
    const im = new window.Image();
    im.onload = () => setImg(im);
    im.src = src;
  }, [src]);

  const ratio = img ? (aspect || img.width / img.height) : 1;
  let dispW = 340, dispH = 340 / ratio;
  if (dispH > 440) { dispH = 440; dispW = 440 * ratio; }
  let x = 0, y = 0, drawW = 0, drawH = 0;
  if (img) {
    const base = (fit === "fill" ? Math.max : Math.min)(dispW / img.width, dispH / img.height);
    const scale = base * zoom;
    drawW = img.width * scale; drawH = img.height * scale;
    x = dispW / 2 - drawW / 2 + off.x;
    y = dispH / 2 - drawH / 2 + off.y;
  }

  const apply = () => {
    let outW, outH;
    if (mode === "full") { outW = 1654; outH = 2339; }
    else if (ratio >= 1) { outW = 1600; outH = Math.round(1600 / ratio); }
    else { outH = 1600; outW = Math.round(1600 * ratio); }
    const k = outW / dispW;
    const canvas = document.createElement("canvas");
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, outW, outH);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, x * k, y * k, drawW * k, drawH * k);
    onApply(canvas.toDataURL("image/jpeg", 0.92));
  };

  const down = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    setOff({ x: drag.current.ox + e.clientX - drag.current.sx, y: drag.current.oy + e.clientY - drag.current.sy });
  };
  const up = () => { drag.current = null; };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/80 p-4 backdrop-blur-sm" data-testid="img-adjust-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between bg-[#0F2042] px-4 py-3 text-white">
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/60">
              {mode === "full" ? "Full-page image · A4" : "Section image"}
            </div>
            <div className="font-display text-sm font-bold">Adjust image — drag to move, zoom to fit</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-white/10" data-testid="img-adjust-close"><X size={16} /></button>
        </header>

        <div className="flex justify-center bg-slate-800 py-4"
          style={{ backgroundImage: "repeating-conic-gradient(#334155 0% 25%, #1e293b 0% 50%)", backgroundSize: "18px 18px" }}>
          {!img ? (
            <div className="flex h-[300px] items-center"><Loader2 size={22} className="animate-spin text-white/60" /></div>
          ) : (
            <div className="relative cursor-move touch-none overflow-hidden bg-white shadow-2xl ring-2 ring-[#F97316]"
              style={{ width: dispW, height: dispH }}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
              data-testid="img-adjust-frame">
              <img src={src} alt="" draggable={false} className="pointer-events-none absolute max-w-none select-none"
                style={{ left: x, top: y, width: drawW, height: drawH }} />
              <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
                {Array.from({ length: 9 }).map((_, gi) => <div key={gi} className="border border-white/20" />)}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full bg-slate-100 p-0.5 text-[10px] font-bold">
              <button onClick={() => { setFit("fill"); setOff({ x: 0, y: 0 }); }} data-testid="img-adjust-fill"
                className={`rounded-full px-3 py-1 ${fit === "fill" ? "bg-[#0F2042] text-white" : "text-slate-500"}`}>Fill (crop)</button>
              <button onClick={() => { setFit("fit"); setOff({ x: 0, y: 0 }); }} data-testid="img-adjust-fit"
                className={`rounded-full px-3 py-1 ${fit === "fit" ? "bg-[#0F2042] text-white" : "text-slate-500"}`}>Fit (whole image)</button>
            </div>
            {mode !== "full" && (
              <select value={aspect} onChange={(e) => { setAspect(Number(e.target.value)); setOff({ x: 0, y: 0 }); setZoom(1); }}
                data-testid="img-adjust-aspect"
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-bold text-slate-600 outline-none">
                {ASPECTS.map((a) => <option key={a.label} value={a.v}>{a.label}</option>)}
              </select>
            )}
          </div>
          <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
            Zoom
            <input type="range" min={0.5} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="img-adjust-zoom" className="flex-1 accent-[#F97316]" />
            <span className="w-10 text-right text-slate-700">{Math.round(zoom * 100)}%</span>
          </label>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => { setZoom(1); setOff({ x: 0, y: 0 }); }} data-testid="img-adjust-reset"
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-bold text-slate-500 hover:border-slate-400">
              <RotateCcw size={11} /> Reset
            </button>
            <button onClick={apply} disabled={!img} data-testid="img-adjust-apply"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#F97316] px-5 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50">
              <Check size={13} /> Apply — PDF will match this
            </button>
          </div>
          <p className="text-center text-[9px] text-slate-400">
            The adjusted image is saved exactly as framed above{mode === "full" ? " (full A4 page)" : ""} — the downloaded PDF shows the same result.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
